/**
 * Dépôts de reprise et liens de dépôt (ARTO-129, `docs/dev/api.md` § 12.15) :
 * les appels de la console, et le transport de l'envoi par morceaux que
 * `@artosera/shared` ordonnance (`envoyerFichiersDepot`).
 *
 * Les morceaux partent au stockage par URL présignée (mode `direct`) ou à
 * l'API (mode `api`), selon ce que l'API annonce : la console n'en décide
 * jamais. En mode `?demo`, aucune requête réseau (`demoDepots.ts`).
 */
import {
  ErreurEnvoiDepot,
  type ModeEnvoiDepot,
  type OuvertureFichierDepot,
  type TransportDepot,
} from '@artosera/shared';
import { ApiError, modeDemo, requete } from './api.js';
import { demoDepots } from './demoDepots.js';

export type EtatInventaire = 'vide' | 'a_faire' | 'en_cours' | 'fait' | 'bloque';

export interface CompteursInventaire {
  acceptes: number;
  refuses: number;
  enAttente: number;
  octetsAcceptes: number;
}

export interface DepotVue {
  id: string;
  galerieId: string;
  dossierId: string | null;
  lienId: string | null;
  libelle: string;
  etat: 'ouvert' | 'ferme' | 'en_purge' | 'purge';
  plafondOctets: number;
  octetsDeclares: number;
  octetsRecus: number;
  fichiers: number;
  inventaire: {
    etat: EtatInventaire;
    raison: string | null;
    faitLe: string | null;
    compteurs: CompteursInventaire | null;
    manifeste: boolean;
  };
  utilisable: boolean;
  envoi: { mode: ModeEnvoiDepot; tailleMorceau: number; envoisParalleles: number };
  operateur: string;
  creeLe: string;
  purgeLe: string | null;
}

/** L'inventaire tourne encore : la liste se relit. */
export function depotEnTravail(depot: Pick<DepotVue, 'inventaire'>): boolean {
  return depot.inventaire.etat === 'a_faire' || depot.inventaire.etat === 'en_cours';
}

export interface EntreeManifeste {
  id: string;
  chemin: string;
  octets: number;
  etat: 'recu' | 'accepte' | 'refuse' | 'en_attente_analyse' | 'deplie';
  motif: string | null;
  detail: string | null;
  sha256: string | null;
  type: { mime: string; ext: string } | null;
}

/** L'index du manifeste : ses compteurs et ses pages, lues une à une. */
export interface Manifeste {
  etabliLe: string;
  utilisable: boolean;
  compteurs: CompteursInventaire;
  pages: { fichiers: number }[];
}

export interface LienVue {
  id: string;
  depotId: string;
  libelle: string;
  etat: 'actif' | 'expire' | 'revoque';
  expireLe: string;
  octetsMax: number;
  octetsDeclares: number;
  octetsRecus: number;
  fichiers: number;
  creePar: string;
  revoqueLe: string | null;
  creeLe: string;
}

export interface NouveauLien {
  libelle: string;
  octetsMax: number;
  dureeJours: number;
}

const GIO = 1024 ** 3;
export const octetsEnGio = (gio: number): number => Math.round(gio * GIO);

const base = (idGalerie: string): string =>
  `/v1/console/galleries/${encodeURIComponent(idGalerie)}/reprise`;
const cheminDepot = (idGalerie: string, depotId: string): string =>
  `${base(idGalerie)}/depots/${encodeURIComponent(depotId)}`;

export async function listerDepots(idGalerie: string): Promise<DepotVue[]> {
  if (modeDemo()) return demoDepots.depots(idGalerie);
  return (await requete<{ depots: DepotVue[] }>(`${base(idGalerie)}/depots`)).depots;
}

export async function creerDepot(
  idGalerie: string,
  corps: { libelle: string; plafondOctets: number },
): Promise<DepotVue> {
  if (modeDemo()) return demoDepots.creer(idGalerie, corps);
  return requete<DepotVue>(`${base(idGalerie)}/depots`, { methode: 'POST', corps });
}

/** Fin d'un lot d'envois, ou relance après une panne d'antivirus. */
export async function inventorierDepot(idGalerie: string, depotId: string): Promise<DepotVue> {
  if (modeDemo()) return demoDepots.inventorier(idGalerie, depotId);
  return requete<DepotVue>(`${cheminDepot(idGalerie, depotId)}/inventorier`, { methode: 'POST' });
}

export async function lireManifeste(idGalerie: string, depotId: string): Promise<Manifeste> {
  if (modeDemo()) return demoDepots.manifeste(idGalerie, depotId);
  return requete<Manifeste>(`${cheminDepot(idGalerie, depotId)}/manifeste`);
}

/** Une page du manifeste, à partir de 1. */
export async function lirePageManifeste(
  idGalerie: string,
  depotId: string,
  page: number,
): Promise<EntreeManifeste[]> {
  if (modeDemo()) return demoDepots.pageManifeste(idGalerie, depotId);
  return (
    await requete<{ fichiers: EntreeManifeste[] }>(
      `${cheminDepot(idGalerie, depotId)}/manifeste?page=${String(page)}`,
    )
  ).fichiers;
}

export async function listerLiens(idGalerie: string): Promise<LienVue[]> {
  if (modeDemo()) return demoDepots.liens(idGalerie);
  return (await requete<{ liens: LienVue[] }>(`${base(idGalerie)}/liens-depot`)).liens;
}

/** L'adresse du lien n'est rendue qu'ici, une fois : l'API n'en garde que l'empreinte. */
export async function creerLien(
  idGalerie: string,
  corps: NouveauLien,
): Promise<{ lien: LienVue; url: string }> {
  if (modeDemo()) return demoDepots.creerLien(idGalerie, corps);
  return requete<{ lien: LienVue; url: string }>(`${base(idGalerie)}/liens-depot`, {
    methode: 'POST',
    corps,
  });
}

export async function revoquerLien(idGalerie: string, lienId: string): Promise<LienVue> {
  if (modeDemo()) return demoDepots.revoquerLien(idGalerie, lienId);
  return requete<LienVue>(`${base(idGalerie)}/liens-depot/${encodeURIComponent(lienId)}`, {
    methode: 'DELETE',
  });
}

// ── Transport de l'envoi ─────────────────────────────────────────────────

/** Une erreur d'API devient une erreur d'envoi : un refus ne se réessaie pas. */
function erreurEnvoi(erreur: unknown): unknown {
  if (!(erreur instanceof ApiError)) return erreur;
  const raison = (erreur.details as { raison?: unknown } | null)?.raison;
  const passagere = erreur.status >= 500 || erreur.status === 408 || erreur.status === 429;
  return new ErreurEnvoiDepot(
    erreur.message,
    !passagere,
    typeof raison === 'string' ? raison : erreur.code,
  );
}

async function relayer<T>(geste: () => Promise<T>): Promise<T> {
  try {
    return await geste();
  } catch (erreur) {
    throw erreurEnvoi(erreur);
  }
}

/**
 * Un morceau, par `XMLHttpRequest` : seul moyen de suivre la progression d'un
 * envoi. Au stockage, sans cookie ni en-tête (l'URL porte sa signature) ; à
 * l'API, avec la session et l'en-tête anti-CSRF.
 */
export function envoyerMorceau(
  cible: { url: string; versApi: boolean },
  corps: Blob,
  progres: (octets: number) => void,
  signal: AbortSignal | undefined,
): Promise<void> {
  return new Promise((resoudre, rejeter) => {
    const requeteXhr = new XMLHttpRequest();
    requeteXhr.open('PUT', cible.url);
    if (cible.versApi) {
      requeteXhr.withCredentials = true;
      requeteXhr.setRequestHeader('X-Artosera-CSRF', '1');
      requeteXhr.setRequestHeader('Content-Type', 'application/octet-stream');
    }
    requeteXhr.upload.onprogress = (evenement) => progres(evenement.loaded);
    requeteXhr.onload = () => {
      if (requeteXhr.status >= 200 && requeteXhr.status < 300) {
        resoudre();
        return;
      }
      // Le stockage refuse (403) une URL périmée : elle se signe de nouveau.
      const passagere = requeteXhr.status >= 500 || requeteXhr.status === 429 || !cible.versApi;
      rejeter(new ErreurEnvoiDepot(`Envoi refusé (${String(requeteXhr.status)}).`, !passagere));
    };
    requeteXhr.onerror = () => rejeter(new ErreurEnvoiDepot('Réseau indisponible.', false));
    signal?.addEventListener('abort', () => requeteXhr.abort(), { once: true });
    requeteXhr.onabort = () => rejeter(new ErreurEnvoiDepot('Envoi interrompu.', true));
    requeteXhr.send(corps);
  });
}

/** Le transport d'un dépôt de la console. */
export function transportConsole(
  idGalerie: string,
  depotId: string,
  signal: AbortSignal,
): TransportDepot<Blob> {
  if (modeDemo()) return demoDepots.transport(idGalerie, depotId);
  const depot = cheminDepot(idGalerie, depotId);
  const fichier = (id: string) => `${depot}/fichiers/${encodeURIComponent(id)}`;
  return {
    ouvrir: (chemin, octets) =>
      relayer(async (): Promise<OuvertureFichierDepot> => {
        const ouvert = await requete<{
          id: string;
          reprise: boolean;
          envoi: { mode: ModeEnvoiDepot } | null;
        }>(`${depot}/fichiers`, { methode: 'POST', corps: { chemin, octets } });
        // Reprise : ce que le stockage a déjà reçu se relit chez lui.
        const dejaRecus =
          ouvert.reprise && ouvert.envoi !== null
            ? (
                await requete<{ recus: { numero: number }[] }>(`${fichier(ouvert.id)}/morceaux`)
              ).recus.map((recu) => recu.numero)
            : [];
        return { fichierId: ouvert.id, mode: ouvert.envoi?.mode ?? 'api', dejaRecus };
      }),
    signer: (fichierId, numeros) =>
      relayer(async () => {
        const { morceaux } = await requete<{ morceaux: { numero: number; url: string }[] }>(
          `${fichier(fichierId)}/morceaux`,
          { methode: 'POST', corps: { numeros } },
        );
        return morceaux;
      }),
    envoyer: (cible, corps, progres) =>
      envoyerMorceau(
        cible.url === null
          ? { url: `${fichier(cible.fichierId)}/morceaux/${String(cible.numero)}`, versApi: true }
          : { url: cible.url, versApi: false },
        corps,
        progres,
        signal,
      ),
    terminer: (fichierId) =>
      relayer(async () => {
        await requete<unknown>(`${fichier(fichierId)}/terminer`, { methode: 'POST' });
      }),
  };
}
